import { AiCompanionApi, TimeslotsApi, UsersApi, type AiCompanionConfirmAttendeeDto } from "@simbelapp/order-sdk";
import { defineStore } from "pinia";
import type { LocationQueryRaw } from "vue-router";
import { ATTENDANCE_USER_PAGE_SIZE, toParticipantOptions } from "~/utils/helpers/attendance/participants";
import { ATTENDANCE_MAX_HOURS, buildReviewRows, unsignedRowsOf } from "~/utils/helpers/attendance/review-rows";
import { asAttendanceRunEnvelope } from "~/utils/helpers/attendance/run-envelope";
import { impliedAttendanceTimeslot } from "~/utils/helpers/attendance/timeslots";
import { attendanceMatchedSessionOf, attendanceSessionLabel } from "~/utils/helpers/attendance/session-label";
import { routeBaseName } from "~/utils/helpers/routeBaseName";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { normalizeCopilotError } from "~/utils/helpers/copilot/errors";
import { CopilotErrorCodeEnum } from "~/utils/enums/copilot/errors.enums";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import {
  AttendanceRouteQueryEnum,
  AttendanceRunLoadEnum,
  AttendanceRunOriginEnum,
  AttendanceRunStatusEnum,
} from "~/utils/enums/attendance-review.enums";
import { ItemType, MicroserviceEnum } from "~/utils/enums/common.enums";
import type {
  IAttendanceMissingRow,
  IAttendanceParticipantOption,
  IAttendanceProposalEnvelope,
  IAttendanceReviewState,
  IAttendanceRunEnvelope,
  IAttendanceRunOpenOptions,
} from "~/utils/interfaces/attendance-review-interfaces";

/** Where "Voir la session" sends the admin. */
const SESSIONS_ROUTE = "/sessions";
const SESSIONS_ROUTE_NAME = "sessions";

/**
 * Drives the "Analyse feuille d'émargement" review: a sheet imported from a session's steps — or
 * dropped in the Copilote, which finds the session itself rather than being handed it — is read by
 * the backend, which returns the attendance it proposes. The admin verifies one row per signature
 * read, rules on the booked collaborators the sheet never mentions, and confirming resumes the
 * workflow that writes it.
 */
export const useAttendanceReviewStore = defineStore("attendanceReview", {
  state: (): IAttendanceReviewState => ({
    blockedError: null,
    isOpen: false,
    isUploadOpen: false,
    stagedFile: null,
    pendingFile: null,
    isAnalyzing: false,
    choosingSessionId: null,
    chosenSessionId: null,
    status: null,
    origin: AttendanceRunOriginEnum.CHAT,
    runId: null,
    sessionId: null,
    timeslotId: null,
    timeslots: [],
    runRequestSequence: 0,
    timeslotRequestSequence: 0,
    document: null,
    proposal: null,
    candidates: [],
    warning: null,
    signatures: [],
    bookedRows: [],
    enrolledUsers: [],
    sessionUsers: [],
    companyUsers: [],
    companyUsersSearch: "",
    companyUsersOffset: 0,
    hasMoreCompanyUsers: true,
    isLoadingUsers: false,
    isMissingModalOpen: false,
    confirmation: null,
  }),
  getters: {
    /**
     * There is a proposal to review. Session choices render directly in the conversation instead
     * of behind this reopen button, and a confirmed run is recapped rather than reopened.
     */
    canOpenReview: (state) => !!state.proposal && !state.confirmation,
    /** A run parked on an ambiguous session match, with candidates the admin can choose from. */
    hasSessionChoice: (state) => state.status === AttendanceRunStatusEnum.SESSION_CHOICE && state.candidates.length > 0,
    /**
     * There is a candidate list to render. It outlives the choice it served: the picked session
     * stays on screen, checked, as the record of what the rest of the answer was built on.
     */
    hasSessionCandidates: (state) => state.candidates.length > 0,
    /**
     * The run on screen is the panel's own. A sheet imported from a session's steps is analysed
     * through a one-shot conversation the panel never shows, so the panel has no answer to hang a
     * card on: offered there, the card gets a bubble of its own (`needsStandaloneExtras`) and lands
     * in the middle of an empty conversation, which is exactly the bug this guards.
     *
     * A run restored from `?attendanceRunId=` counts as the panel's whichever screen started it:
     * the upload modal is one attempt and is gone, while the panel is mounted at the root and its
     * card is then the only way back into a run the admin closed.
     */
    isChatRun: (state) => state.origin === AttendanceRunOriginEnum.CHAT,
    /**
     * A run is still on screen, and only a reset clears it. Keyed on the run rather than on the
     * conversation because the two come apart: choosing a session resumes the workflow over REST,
     * which writes nothing into the chat, so a reload restores the run beside an empty conversation
     * — and the panel then offered its card with no way to dismiss it.
     */
    hasRunOnScreen(): boolean {
      return (
        this.isChatRun && (this.candidates.length > 0 || !!this.proposal || !!this.confirmation || !!this.blockedError)
      );
    },
    /**
     * Whether the conversation has an attendance card to show *inside* an assistant bubble. The
     * confirmed recap is deliberately out: it is its own card in the panel, and counting it here
     * would open an empty bubble beside it. `busy` is the chat's state rather than the run's, so it
     * is handed in.
     */
    hasChatCards(): (busy: boolean) => boolean {
      return (busy) =>
        this.isChatRun && (this.hasSessionCandidates || !!this.blockedError || (this.canOpenReview && !busy));
    },
    /**
     * The confirmed recap, which the panel renders as an assistant message of its own. Out for an
     * imported sheet: the admin is already on the session it was written to, so « Voir la session »
     * has nowhere to send them and the toast is the whole feedback that flow needs.
     */
    hasChatRecap(): boolean {
      return this.isChatRun && !!this.confirmation;
    },
    /** Everyone booked on the session, as the proposal reported the roster. */
    enrolledUserIds: (state) => new Set(state.enrolledUsers.map((user) => user.userId)),
    /** Collaborators already tied to a sheet line — nobody may be declared twice. */
    pairedUserIds: (state) =>
      new Set(state.signatures.map((row) => row.userId).filter((userId): userId is string => !!userId)),
    /**
     * What the confirm modal rules on. A booked collaborator the payload leaves out is recorded as
     * absent by the backend, so every one of them has to be offered here first — a pairing the admin
     * clears included, which is why the list is derived from the rows rather than from the analysis.
     */
    unsignedRows(state): IAttendanceMissingRow[] {
      return unsignedRowsOf(state.bookedRows, state.signatures);
    },
    /**
     * The session does not settle its own slot, so the admin has to: confirming ends one timeslot,
     * and the sheet's hours are read against the whole session rather than against a slot. True of
     * a session split over several, and of a single one already emargé — see `impliedAttendanceTimeslot`.
     */
    needsTimeslotChoice: (state) => state.timeslots.length > 0 && !impliedAttendanceTimeslot(state.timeslots),
    /** That choice, still unanswered — nothing may be written until it is. */
    isTimeslotChoicePending(): boolean {
      return this.needsTimeslotChoice && !this.timeslotId;
    },
    /** Hours the session expects, shown next to every input. */
    totalHours: (state) => state.proposal?.totalHours ?? 0,
    /** "Session EXCEL AVANCÉ · 12 et 13 mars" — which sheet the admin is reviewing. */
    sessionLabel: (state) => attendanceSessionLabel(state.proposal),
    /** The same session, split in two, for the card that has it checked before the review opens. */
    matchedSession: (state) => attendanceMatchedSessionOf(state.proposal),
  },
  actions: {
    /** A terminal backend refusal replaces every action that could resume the rejected run. */
    blockAttendance(value: unknown): boolean {
      const error = normalizeCopilotError(value);
      if (error.code !== CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION) return false;

      this.claimRun();
      this.claimTimeslots();
      this.blockedError = error;
      this.proposal = null;
      this.status = null;
      this.candidates = [];
      this.signatures = [];
      // Cleared with the signatures, or `unsignedRows` would report the whole roster as unsigned.
      this.bookedRows = [];
      this.timeslots = [];
      this.timeslotId = null;
      this.confirmation = null;
      this.isMissingModalOpen = false;
      this.isAnalyzing = false;
      this.choosingSessionId = null;
      this.isOpen = true;
      void this.syncRoute(null);

      return true;
    },

    /** Opens the verification screen on a streamed proposal. */
    openReview(response: IAttendanceProposalEnvelope) {
      this.openRun(
        {
          ...response,
          status: AttendanceRunStatusEnum.PROPOSAL,
          candidates: [],
          warning: null,
        },
        { openReviewScreen: true },
      );
    },

    /**
     * Opens whichever screen the persisted workflow is waiting on.
     *
     * `origin` is the screen that started the run, and it decides what the assistant panel claims —
     * see `isChatRun`. It defaults to the chat because every caller but the session-steps import is
     * one, restoring from the route included.
     */
    openRun(response: IAttendanceRunEnvelope, options: IAttendanceRunOpenOptions = {}) {
      const { withRoute = true, origin = AttendanceRunOriginEnum.CHAT, openReviewScreen = true } = options;

      this.claimRun();
      this.claimTimeslots();
      this.choosingSessionId = null;
      this.blockedError = null;

      this.origin = origin;
      this.runId = response.runId;
      this.sessionId = response.sessionId;
      this.timeslotId = response.timeslotId;
      this.status = response.status;
      this.candidates = [...response.candidates];
      this.chosenSessionId = null;
      this.warning = response.warning;

      // A document from the API beats the local stand-in: it survives a reload, the object URL does not.
      if (response.document) {
        this.releaseDocument();
        this.document = response.document;
      }

      if (response.status === AttendanceRunStatusEnum.SESSION_CHOICE) {
        this.proposal = null;
        this.signatures = [];
        this.bookedRows = [];
        this.enrolledUsers = [];
        this.sessionUsers = [];
        // The slots belonged to the session being corrected away from; the next one brings its own.
        this.timeslots = [];
        this.isOpen = false;
      } else if (response.proposal) {
        const { signatures, bookedRows } = buildReviewRows(response.proposal);
        const roster = toParticipantOptions(response.proposal.sessionUsers ?? []);

        this.proposal = response.proposal;
        this.signatures = signatures;
        this.bookedRows = bookedRows;
        this.enrolledUsers = roster;
        // The picker's first group, whole: it hides the lines already paired itself.
        this.sessionUsers = roster;
        this.confirmation = null;
        this.isMissingModalOpen = false;
        // A proposal the admin has not asked to see yet stays behind its card — see `openReviewScreen`.
        this.isOpen = openReviewScreen;
        /*
         * The slots belong to the session, which a correction changes: whatever was picked for the
         * previous one is gone, and the new one's slots are asked for from scratch.
         */
        this.timeslots = [];
        void this.loadTimeslots();
      }

      // The marker says a run is recoverable; the flag says it is waiting in the conversation, so a
      // reload puts the admin back in front of its card instead of skipping straight to the review.
      if (response.runId && withRoute) void this.syncRoute(response.runId, !this.isOpen);
    },

    /**
     * Claims the screen for the request about to start. Any request started afterwards invalidates
     * this ticket, so a slow answer to an older question drops instead of overwriting the newer one.
     */
    claimRun(): number {
      this.runRequestSequence += 1;

      return this.runRequestSequence;
    },

    /** Whether the request holding this ticket is still the one the screen is waiting on. */
    ownsRun(ticket: number): boolean {
      return ticket === this.runRequestSequence;
    },

    /** Claims the slot list for the load about to start; see `claimRun` for why a ticket is needed. */
    claimTimeslots(): number {
      this.timeslotRequestSequence += 1;

      return this.timeslotRequestSequence;
    },

    ownsTimeslots(ticket: number): boolean {
      return ticket === this.timeslotRequestSequence;
    },

    /**
     * The matched session's slots. Confirming ends exactly one of them, so the admin answers which —
     * unless the session settles it itself, which only its single still-undeclared slot does.
     *
     * Ticketed rather than guarded on the session alone: two loads can be in flight for the *same*
     * session — `openRun` starts one, and answering "Confirmer" before it lands starts a second
     * through `resolveTimeslotId`. The older one finishing last used to refill the list and clear
     * the slot the admin had picked in between.
     */
    async loadTimeslots(): Promise<void> {
      const sessionId = this.sessionId;
      if (!sessionId) return;

      const ticket = this.claimTimeslots();
      try {
        const timeslotsApi = await useSDKApi(MicroserviceEnum.ORDERS, TimeslotsApi);
        const timeslots = await timeslotsApi.getItemTimeslots({ itemId: sessionId });
        // A later load owns the list, and the session may have changed under this answer entirely.
        if (!this.ownsTimeslots(ticket) || sessionId !== this.sessionId) return;

        this.timeslots = timeslots ?? [];
        // An answer the admin has already given survives a refresh that still offers it.
        const isPickedStillOffered = this.timeslots.some((timeslot) => timeslot.timeslotId === this.timeslotId);

        this.timeslotId = isPickedStillOffered
          ? this.timeslotId
          : (impliedAttendanceTimeslot(this.timeslots)?.timeslotId ?? null);
      } catch {
        // Confirming asks again, and refuses rather than guessing: see `resolveTimeslotId`.
        if (this.ownsTimeslots(ticket) && sessionId === this.sessionId) this.timeslots = [];
      }
    },

    /**
     * The sheet the admin dropped, kept aside so the viewer has something to render.
     * ponytail: replace with the document the proposal should carry (`{ fileName, url }`, as the
     * program review already returns) — an object URL dies with the tab and survives no reload.
     */
    setPendingDocument(file: File) {
      this.releaseDocument();
      this.pendingFile = file;
      this.document = { fileName: file.name, url: URL.createObjectURL(file) };
    },

    /**
     * Opens the streamed result again after the admin closed it. The card that offers it is history
     * — the run may have moved on since, most of all through a session corrected in the chat — so
     * the current state is read back before anything reopens.
     *
     * Only a run read back whole reopens. A refused read says so — the run may simply be gone,
     * confirmed from another tab or past its suspension gate, both of which answer 404, and
     * reopening it would let the admin fill in hours that confirming then refuses, losing the lot.
     * A superseded read says nothing: a newer request owns the screen, and forcing the review open
     * over it would show the very run that request is busy replacing.
     *
     * The proposal is kept rather than cleared: `useSDKApi` drops the HTTP status, so a network blip
     * cannot be told from a 404, and destroying the run on one would be the worse half of the trade.
     */
    async openPendingReview(): Promise<void> {
      if (this.blockedError) return;
      this.isAnalyzing = true;
      try {
        if (this.runId) {
          const outcome = await this.reloadRun(this.runId);

          if (outcome !== AttendanceRunLoadEnum.LOADED) {
            if (outcome === AttendanceRunLoadEnum.FAILED) {
              useFeedback().error("L'émargement en cours n'a pas pu être rechargé", "small");
            }

            return;
          }
        }
        // A proposal that streamed without a run id of its own has nothing to read back.
        if (!this.proposal) return;

        this.isOpen = true;
        if (this.runId) await this.syncRoute(this.runId);
      } finally {
        this.isAnalyzing = false;
      }
    },

    /**
     * Reads a run back and reopens whichever screen it is parked on. Silent on failure — the caller
     * decides what a run it could not read means, and `GET` answers 404 for a run that is simply no
     * longer suspended, which is not an error worth showing on its own.
     */
    async reloadRun(runId: string, options: IAttendanceRunOpenOptions = {}): Promise<AttendanceRunLoadEnum> {
      const ticket = this.claimRun();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AiCompanionApi);
        const response = await apiInstance.aiCompanionGetAttendanceRun({ runId });
        if (!this.ownsRun(ticket)) return AttendanceRunLoadEnum.SUPERSEDED;

        const run = asAttendanceRunEnvelope(response);
        if (!run) return AttendanceRunLoadEnum.FAILED;

        this.openRun(run, { withRoute: false, ...options });

        return AttendanceRunLoadEnum.LOADED;
      } catch {
        return this.ownsRun(ticket) ? AttendanceRunLoadEnum.FAILED : AttendanceRunLoadEnum.SUPERSEDED;
      }
    },

    /**
     * "Importer la feuille d'émargement" on a session's steps: a drop zone, and nothing else. The
     * sheet is sent through the assistant's stream (`useAttendanceSheetImport`) rather than through
     * `POST /ai-companion/attendance/analyze`, because only a run started from the stream is
     * persisted — and only a persisted run can be confirmed afterwards. The proposal then opens the
     * same review the chat opens.
     */
    openUpload() {
      this.isUploadOpen = true;
    },

    stageFile(file: File | null) {
      this.stagedFile = file;
    },

    closeUpload() {
      this.isUploadOpen = false;
      this.stagedFile = null;
    },

    /** Resumes a chat run parked on an ambiguous session match. */
    async chooseSession(sessionId: string): Promise<boolean> {
      if (!this.runId || !this.candidates.some((candidate) => candidate.sessionId === sessionId)) return false;

      const ticket = this.claimRun();

      this.choosingSessionId = sessionId;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AiCompanionApi);
        const response = await apiInstance.aiCompanionChooseAttendanceSession({
          runId: this.runId,
          aiCompanionAttendanceChooseSessionDto: { sessionId },
        });
        // A correction re-detected meanwhile asked a new question: this answers the previous one.
        if (!this.ownsRun(ticket)) return false;

        // The proposal envelope carries no candidates, and the list is the record of the choice.
        const candidates = this.candidates;

        this.openReview({
          runId: response.runId,
          sessionId: response.sessionId,
          timeslotId: null,
          document: null,
          proposal: response.proposal,
        });
        this.candidates = candidates;
        this.chosenSessionId = sessionId;

        return true;
      } catch (error) {
        if (!this.ownsRun(ticket)) return false;
        if (this.blockAttendance(error)) return false;
        useFeedback().error("La session n'a pas pu être sélectionnée", "small");

        return false;
      } finally {
        if (this.ownsRun(ticket)) this.choosingSessionId = null;
      }
    },

    /** Restores a proposal or pending session choice after a browser reload. */
    async restoreFromRoute(): Promise<void> {
      const router = useRouter();
      const rawRunId = router.currentRoute.value.query[AttendanceRouteQueryEnum.RUN_ID];
      const runId = Array.isArray(rawRunId) ? rawRunId[0] : rawRunId;
      if (typeof runId !== "string" || !runId) return;

      /*
       * A run parked in the conversation is restored parked: reopening its review would hand back a
       * pre-filled attendance table for a session the admin never confirmed. Compared as the string
       * a URL actually carries — `?booking=true` and `?embed=true` are read the same way — and
       * unlike the run id it needs no single-value guard: anything else simply is not the flag.
       */
      const isAwaitingCheck = router.currentRoute.value.query[AttendanceRouteQueryEnum.AWAITING_CHECK] === "true";

      this.isAnalyzing = true;
      try {
        // Only a real failure clears the route: a run superseded by a newer request is not missing,
        // and stripping the query would drop the run the newer request is busy opening.
        if ((await this.reloadRun(runId, { openReviewScreen: !isAwaitingCheck })) !== AttendanceRunLoadEnum.FAILED) {
          return;
        }

        await this.syncRoute(null);
        useFeedback().error("L'émargement en cours n'a pas pu être rechargé", "small");
      } finally {
        this.isAnalyzing = false;
      }
    },

    /**
     * Writes or strips the run id on the current route, so a run survives a reload. `awaitingCheck`
     * records that it is parked in the conversation rather than open on screen — see the enum.
     */
    async syncRoute(runId: string | null, awaitingCheck = false): Promise<void> {
      const router = useRouter();
      const routeKeys: string[] = [AttendanceRouteQueryEnum.RUN_ID, AttendanceRouteQueryEnum.AWAITING_CHECK];
      const query = Object.fromEntries(
        Object.entries(router.currentRoute.value.query).filter(([key]) => !routeKeys.includes(key)),
      ) as LocationQueryRaw;
      if (runId) query[AttendanceRouteQueryEnum.RUN_ID] = runId;
      if (runId && awaitingCheck) query[AttendanceRouteQueryEnum.AWAITING_CHECK] = "true";

      try {
        await router.replace({ query });
      } catch {
        // Route bookkeeping must never prevent the already prepared modal from opening or closing.
      }
    },

    /**
     * "Inscrits à cette session", refreshed every time a picker opens: bookings may have moved since
     * the sheet was analysed. Asked for whole — the exclusion the endpoint offers is left out on
     * purpose, because the picker already knows which lines are paired and drops them live. An
     * answer that fails or comes back empty therefore leaves the group the proposal shipped on
     * screen, where it used to wipe it and the suggestions simply vanished.
     */
    async loadSessionUsers(): Promise<void> {
      if (!this.sessionId) return;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AiCompanionApi);
        const users = await apiInstance.aiCompanionGetSessionUsersExcluding({
          sessionId: this.sessionId,
          aiCompanionAttendanceSessionUsersDto: { excludedUserIds: [] },
        });

        if (users?.length) this.sessionUsers = toParticipantOptions(users);
      } catch {
        // The roster the proposal shipped stays on screen: a stale group beats an empty picker.
      }
    },

    /**
     * "Tous les utilisateurs", first page. Searched server-side — it matches on email and employee
     * number too, which is why the picker never re-filters this group itself.
     */
    async searchCompanyUsers(search = ""): Promise<void> {
      this.companyUsersSearch = search;
      this.companyUsersOffset = 0;
      this.hasMoreCompanyUsers = true;
      this.isLoadingUsers = true;
      try {
        const page = await this.fetchCompanyUsersPage(search, 0);
        // A search started meanwhile owns the list: debouncing narrows this window, it never closes
        // it, and "a" landing after "ab" would leave the picker showing options nobody asked for.
        if (search !== this.companyUsersSearch) return;

        this.companyUsers = page;
        this.companyUsersOffset = ATTENDANCE_USER_PAGE_SIZE;
        this.hasMoreCompanyUsers = page.length >= ATTENDANCE_USER_PAGE_SIZE;
      } catch {
        if (search !== this.companyUsersSearch) return;

        this.companyUsers = [];
        this.hasMoreCompanyUsers = false;
      } finally {
        // The newer search owns the spinner too — it is still loading.
        if (search === this.companyUsersSearch) this.isLoadingUsers = false;
      }
    },

    /** Next page of "Tous les utilisateurs" — the picker asks when its list is scrolled to the end. */
    async loadMoreCompanyUsers(): Promise<void> {
      if (this.isLoadingUsers || !this.hasMoreCompanyUsers) return;

      const search = this.companyUsersSearch;
      const offset = this.companyUsersOffset;
      this.isLoadingUsers = true;
      try {
        const page = await this.fetchCompanyUsersPage(search, offset);
        // A search started meanwhile owns the list: this page answers a question nobody is asking.
        if (search !== this.companyUsersSearch) return;

        const known = new Set(this.companyUsers.map((user) => user.userId));

        this.companyUsers = [...this.companyUsers, ...page.filter((user) => !known.has(user.userId))];
        this.companyUsersOffset = offset + ATTENDANCE_USER_PAGE_SIZE;
        this.hasMoreCompanyUsers = page.length >= ATTENDANCE_USER_PAGE_SIZE;
      } catch {
        // Whatever is on screen stays; a failed page must not make the list ask for it forever.
        this.hasMoreCompanyUsers = false;
      } finally {
        this.isLoadingUsers = false;
      }
    },

    /** One page of the company search, as the picker's second group wants it. */
    async fetchCompanyUsersPage(search: string, offset: number): Promise<IAttendanceParticipantOption[]> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
      const response = await apiInstance.getUsersCompany({
        lazyLoad: true,
        enableRolesFilters: false,
        limit: ATTENDANCE_USER_PAGE_SIZE,
        offset,
        search,
      });

      return (response?.data ?? []).map((user) => ({ userId: user.userId, displayValue: user.displayValue }));
    },

    /**
     * Footer "Confirmer". The booked collaborators the sheet never mentions are ruled on first —
     * leaving their hours at 0 is what declares them absent, and that has to be a deliberate answer
     * rather than a side effect of validating the table.
     */
    async requestConfirmation(): Promise<void> {
      if (this.blockedError) return;
      if (this.unsignedRows.length) {
        this.isMissingModalOpen = true;

        return;
      }

      await this.confirmAttendance();
    },

    /** "Retour au tableau" — the admin goes back to the signatures without writing anything. */
    closeMissingModal(): void {
      this.isMissingModalOpen = false;
    },

    /**
     * Resumes the detect workflow with the verified attendance. Sheet lines left without a
     * collaborator are simply not sent — an unrecognised name declares nobody.
     */
    async confirmAttendance(): Promise<boolean> {
      if (this.blockedError) return false;
      const feedback = useFeedback();

      // A bare proposal opens for review but cannot be written back: there is no run to resume.
      if (!this.runId || !this.sessionId) {
        feedback.error("Cet émargement ne peut pas être confirmé : analyse incomplète", "small");

        return false;
      }

      const ticket = this.claimRun();
      try {
        const timeslotId = await this.resolveTimeslotId();
        if (!this.ownsRun(ticket)) return false;

        if (!timeslotId) {
          // Told apart on purpose: one is a session to fix, the other an answer still to give.
          feedback.error(
            this.needsTimeslotChoice ? "Sélectionnez le créneau à émarger" : "Aucun créneau de formation trouvé",
            "small",
          );

          return false;
        }

        const signed = this.signatures.filter((row) => !!row.userId);
        const unsigned = this.unsignedRows;
        const attendees: AiCompanionConfirmAttendeeDto[] = [
          ...signed.map((row) => ({ userId: row.userId as string, attendedHours: row.attendedHours })),
          ...unsigned.map((row) => ({ userId: row.userId, attendedHours: row.attendedHours })),
        ];

        const hasInvalidHours = attendees.some(
          ({ attendedHours }) =>
            !Number.isFinite(attendedHours) || attendedHours < 0 || attendedHours > ATTENDANCE_MAX_HOURS,
        );
        if (hasInvalidHours) {
          feedback.error(`Le temps effectué doit être compris entre 0 et ${ATTENDANCE_MAX_HOURS} heures`, "small");

          return false;
        }

        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AiCompanionApi);
        if (!this.ownsRun(ticket)) return false;
        const response = await apiInstance.aiCompanionConfirmAttendanceRun({
          runId: this.runId,
          aiCompanionAttendanceConfirmDto: { timeslotId, attendees },
        });
        if (!this.ownsRun(ticket)) return false;

        /*
         * What the panel recaps, as the API reports it: it alone knows which signatories it actually
         * booked, and it also zeroes booked attendees left out of the payload — neither of which the
         * rows on screen can tell. The local count stands in only for a backend that answers without
         * those lists.
         *
         * The local reading of "added" is off the roster rather than off the row's status: a line the
         * model paired keeps saying "associé par l'IA", and confirming still adds whoever it points at.
         */
        const addedNames = signed
          .filter((row) => !this.enrolledUserIds.has(row.userId as string))
          .map((row) => row.displayValue ?? row.nameAttendee);
        const absentNames = [
          ...signed.filter((row) => row.attendedHours <= 0).map((row) => row.displayValue ?? row.nameAttendee),
          ...unsigned.filter((row) => row.attendedHours <= 0).map((row) => row.displayValue),
        ];

        this.confirmation = {
          sessionId: response?.sessionId ?? this.sessionId,
          declaredCount: response?.declaredCount ?? attendees.length,
          addedNames: response?.addedToSession ?? addedNames,
          absentNames: response?.notThere ?? absentNames,
        };

        this.isMissingModalOpen = false;
        feedback.success("Vos modifications ont été sauvegardées", "small");
        this.close();

        return true;
      } catch (error) {
        if (!this.ownsRun(ticket)) return false;
        if (this.blockAttendance(error)) return false;
        feedback.error("Une erreur est survenue", "small");

        return false;
      }
    },

    /** "Voir la session" on the recap card. */
    async viewSession(requestedSessionId?: string | null): Promise<void> {
      const sessionId = requestedSessionId ?? this.confirmation?.sessionId;
      if (!sessionId) return;
      this.close();
      this.closeUpload();

      // /sessions opens the detail on mount only, so from there the modal has to be asked for.
      if (routeBaseName(useRouter().currentRoute.value.name) === SESSIONS_ROUTE_NAME) {
        await useSessionDetailsStore().showMySession(sessionId, ItemType.SESSION);

        return;
      }

      await navigateTo({ path: SESSIONS_ROUTE, query: { id: sessionId, type: ItemType.SESSION } });
    },

    /** The slot the footer's dropdown settled on. */
    pickTimeslot(timeslotId: string | null): void {
      this.timeslotId = timeslotId;
    },

    /**
     * The slot the attendance ends: the one the admin picked, or the session's only one. Never the
     * first of several — the sheet's hours are read against the whole session, so picking a slot on
     * the admin's behalf would declare hours nobody verified against it.
     */
    async resolveTimeslotId(): Promise<string | null> {
      if (this.timeslotId) return this.timeslotId;
      // The slots are loaded when the proposal opens; asked again only if that load failed.
      if (!this.timeslots.length) await this.loadTimeslots();

      return impliedAttendanceTimeslot(this.timeslots)?.timeslotId ?? null;
    },

    /** The viewer URL is an object URL while the API returns no document — or it leaks. */
    releaseDocument() {
      if (this.document?.url.startsWith("blob:")) URL.revokeObjectURL(this.document.url);
    },

    /** Closes the review but keeps the document: the proposal can be reopened from the chat card. */
    close() {
      this.isOpen = false;
      this.isMissingModalOpen = false;
      void this.syncRoute(null);
    },

    /** "Nouvelle discussion" — the streamed proposals are gone, so nothing may reopen from them. */
    reset() {
      const runSequence = this.runRequestSequence + 1;
      const timeslotSequence = this.timeslotRequestSequence + 1;
      this.releaseDocument();
      this.$reset();
      this.runRequestSequence = runSequence;
      this.timeslotRequestSequence = timeslotSequence;
      void this.syncRoute(null);
    },
  },
});
